import { LandingMetaPixel } from '../types'
import { DEFAULT_META_PIXEL_EVENTS } from '../data/metaPixel'

/**
 * Meta Pixel (Facebook/Instagram) por organización, sin librerías externas.
 *
 * Rendimiento: el script oficial de Meta se inyecta de forma asíncrona y
 * solo cuando existe un Pixel ID válido y activado — sin Pixel configurado,
 * cero peticiones a Meta y ningún efecto sobre la carga de la landing.
 *
 * Seguridad: el Pixel ID escrito por el usuario nunca se inserta como texto
 * dentro de un `<script>`. Solo se acepta si supera `isValidPixelId` (solo
 * dígitos) y siempre se pasa como argumento tipado a `fbq(...)`, nunca
 * interpolado en una cadena de HTML o JS.
 */

/** Un Pixel ID de Meta son solo dígitos (normalmente 15-16). */
const META_PIXEL_ID_REGEX = /^\d{5,20}$/

export function isValidPixelId(value?: string | null): boolean {
  return Boolean(value && META_PIXEL_ID_REGEX.test(value.trim()))
}

export interface MetaPixelResolved {
  /** null cuando no hay Pixel activo: ningún componente debe cargar nada. */
  pixelId: string | null
  enabled: boolean
  events: Required<import('../types').MetaPixelEvents>
}

/**
 * Resuelve la configuración de una landing a algo directamente usable.
 * Nunca confía en el texto sin validar: un Pixel ID con formato incorrecto
 * se descarta como si no existiera.
 */
export function resolveMetaPixel(config?: LandingMetaPixel | null): MetaPixelResolved {
  const raw = config?.pixelId?.trim()
  const pixelId = isValidPixelId(raw) ? (raw as string) : null
  const enabled = Boolean(config?.enabled) && Boolean(pixelId)
  return {
    pixelId: enabled ? pixelId : null,
    enabled,
    events: { ...DEFAULT_META_PIXEL_EVENTS, ...(config?.events ?? {}) },
  }
}

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[][]
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: unknown
  }
}

const initializedPixels = new Set<string>()

/**
 * Código oficial de Meta Pixel, adaptado a TypeScript. Se inserta como
 * `<script async>` mediante `insertBefore` (nunca `document.write`), así que
 * no bloquea el render inicial ni el hero. Es idempotente: si `window.fbq`
 * ya existe, no vuelve a inyectar nada.
 */
function ensureFbqLoader(): void {
  if (typeof window === 'undefined' || window.fbq) return
  const fbq: Fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args)
    else fbq.queue!.push(args)
  }
  if (!window._fbq) window._fbq = fbq
  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  window.fbq = fbq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) firstScript.parentNode.insertBefore(script, firstScript)
  else document.head.appendChild(script)
}

/** Inicializa un Pixel ID una sola vez, sin importar cuántas veces se llame. */
export function initMetaPixel(resolved: MetaPixelResolved): void {
  if (!resolved.enabled || !resolved.pixelId) return
  if (initializedPixels.has(resolved.pixelId)) return
  initializedPixels.add(resolved.pixelId)
  ensureFbqLoader()
  window.fbq?.('init', resolved.pixelId)
}

/** Un fallo de Meta nunca debe romper el flujo real del visitante (envío, WhatsApp…). */
function safeTrack(resolved: MetaPixelResolved, event: string, custom: boolean): void {
  if (!resolved.enabled || !resolved.pixelId) return
  try {
    initMetaPixel(resolved)
    window.fbq?.(custom ? 'trackCustom' : 'track', event)
  } catch {
    // Silencioso a propósito: nunca debe impedir el envío o abrir WhatsApp.
  }
}

/** A. Landing visitada. */
export function trackMetaPageView(resolved: MetaPixelResolved): void {
  if (resolved.events.pageView === false) return
  safeTrack(resolved, 'PageView', false)
}

/** B/C. Lead completado (formulario guardado correctamente). */
export function trackMetaLead(resolved: MetaPixelResolved): void {
  if (resolved.events.lead === false) return
  safeTrack(resolved, 'Lead', false)
}

/** D. Clic en el botón de WhatsApp de la página de gracias. */
export function trackMetaWhatsapp(resolved: MetaPixelResolved): void {
  if (resolved.events.whatsapp === false) return
  safeTrack(resolved, 'ContactWhatsApp', true)
}

/**
 * E. Preparado para cuando exista una confirmación real de reserva (por
 * ejemplo, escuchando el `postMessage` `calendly.event_scheduled` de un
 * iframe de Calendly). No se llama desde ningún sitio todavía: el bloque de
 * calendario actual admite cualquier URL de agenda externa y no hay forma
 * fiable de saber si el visitante llegó a reservar.
 */
export function trackMetaSchedule(resolved: MetaPixelResolved): void {
  if (resolved.events.schedule === false) return
  safeTrack(resolved, 'Schedule', false)
}
